import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import { useState, useMemo, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Image,
} from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  getBlocksByKind,
  type ContentBlock,
  type MedicationBody,
} from "@src/packages/content";
import { useTranslations, useLocale } from "@src/packages/i18n";
import { EMBER } from "../../../onboarding/theme";
import type { ChatStackParamList } from "../../types";
import {
  MoonFilterChip,
  MoonSearchInput,
  BackLink,
  cardSurface,
} from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import { useSmartBack, getBackLabel } from "../../hooks/useSmartBack";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";
import { semanticColors, colors } from "@louise/theme/tokens";

const pillCapsuleImage =
  require("../../../assets/images/pill-capsule.png") as number;
const vialSyringeImage =
  require("../../../assets/images/vial-syringe.png") as number;
const injectionPenImage =
  require("../../../assets/images/injection-pen.png") as number;

function getMedImage(body: MedicationBody): number {
  const forms = body.commonForms;
  if (forms.includes("pen")) return injectionPenImage;
  if (forms.includes("vial")) return vialSyringeImage;
  return pillCapsuleImage;
}

/* ─── Filter config ────────────────────────────────────────────── */

// Maps the raw drug-class value coming from CMS to a stable i18n key.
const DRUG_CLASS_I18N_KEYS: Record<string, string> = {
  Gonadotropin: "gonadotropin",
  "GnRH Antagonist": "gnrh_antagonist",
  "GnRH Agonist": "gnrh_agonist",
  "Trigger Shot": "trigger_shot",
  Progesterone: "progesterone",
  Estrogen: "estrogen",
  "Oral Ovulation Induction": "oral_ovulation_induction",
  Anticoagulant: "anticoagulant",
  Supplement: "supplement",
};

type Props = NativeStackScreenProps<ChatStackParamList, "Medications">;

export default function MedicationsScreen({ route, navigation }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicationsScreen");
  const returnTab = route.params?.returnTab;
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Learn",
    fallbackScreen: "Learn",
  });
  const backLabel =
    returnTab && returnTab !== "Learn"
      ? getBackLabel(returnTab, "Learn", t("you.shared.back"))
      : t("learn.back_to_learn");
  const { locale } = useLocale();
  const [search, setSearch] = useState("");
  const [activeClass, setActiveClass] = useState<string | null>(null);

  const medications = useMemo(() => getBlocksByKind("medication"), []);

  // Reads body.frBody when active locale is FR — falls back to EN otherwise.
  // The MedicationFactsheetSheet detail view already does this; the
  // encyclopedia card list needs the same treatment for genericName,
  // drugClass, and purpose so cards aren't mixed-language.
  const fieldsFor = useCallback(
    (body: MedicationBody) => {
      const fr = locale === "fr" ? body.frBody : undefined;
      return {
        genericName: fr?.genericName ?? body.genericName,
        drugClass: fr?.drugClass ?? body.drugClass,
        purpose: fr?.purpose ?? body.purpose,
      };
    },
    [locale],
  );

  // Derive available drug classes from data
  const drugClasses = useMemo(() => {
    const classes = new Set<string>();
    for (const med of medications) {
      if (med.body.drugClass) classes.add(med.body.drugClass);
    }
    return Array.from(classes).sort();
  }, [medications]);

  const filtered = useMemo(() => {
    let result = medications;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((m) => {
        const f = fieldsFor(m.body);
        return (
          m.title.toLowerCase().includes(q) ||
          f.genericName.toLowerCase().includes(q) ||
          f.drugClass.toLowerCase().includes(q) ||
          f.purpose.toLowerCase().includes(q) ||
          m.body.brandsByCountry?.some((entry) =>
            entry.brands.some((b) => b.toLowerCase().includes(q)),
          )
        );
      });
    }

    if (activeClass) {
      // activeClass is always the EN drugClass (the filter chips use raw EN
      // values as identity); match against base body.drugClass not the
      // locale-aware version.
      result = result.filter((m) => m.body.drugClass === activeClass);
    }

    result.sort((a, b) => a.title.localeCompare(b.title));
    return result;
  }, [medications, search, activeClass, fieldsFor]);

  // Navigate to the deep-linkable factsheet route so the URL reflects the
  // open medication (shareable / back-button friendly).
  const onItemPress = useCallback(
    (item: ContentBlock<"medication">) => {
      capturePress(pressLabel(item.title), "learn_medication_card");
      navigation.navigate("MedicationDetail", { medicationId: item.id });
    },
    [capturePress, navigation],
  );

  // Pair items into rows of 2
  const rows = useMemo(() => {
    const result: ContentBlock<"medication">[][] = [];
    for (let i = 0; i < filtered.length; i += 2) {
      result.push(filtered.slice(i, i + 2));
    }
    return result;
  }, [filtered]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <BackLink
          label={backLabel}
          action="learn_encyclopedia_back"
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 38,
            fontWeight: "700",
            color: colors.deepBrown,
            lineHeight: 42,
            letterSpacing: -0.5,
            ...(Platform.OS === "web" && {
              ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }),
          }}
        >
          {t("learn.encyclopedia_screens.medications.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 21,
          }}
        >
          {t("learn.encyclopedia_screens.medications.subtitle")}
        </Text>

        {/* Search bar */}
        <MoonSearchInput
          value={search}
          onChangeText={setSearch}
          placeholder={t(
            "learn.encyclopedia_screens.medications.search_placeholder",
          )}
          onClear={() => setSearch("")}
          tone="glass"
          style={{ marginTop: 18 }}
        />

        {/* Drug class filter chips */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 8,
            paddingVertical: 14,
          }}
        >
          <MoonFilterChip
            label={t("learn.encyclopedia_screens.all")}
            selected={!activeClass}
            onPress={() => setActiveClass(null)}
            tone="glass"
            showCheck={false}
          />
          {drugClasses.map((dc) => {
            const isActive = activeClass === dc;
            return (
              <MoonFilterChip
                key={dc}
                label={
                  DRUG_CLASS_I18N_KEYS[dc]
                    ? t(
                        `learn.encyclopedia_screens.medications.drug_classes.${DRUG_CLASS_I18N_KEYS[dc]}`,
                      )
                    : dc
                }
                selected={isActive}
                onPress={() => setActiveClass(isActive ? null : dc)}
                tone="glass"
                showCheck={false}
              />
            );
          })}
        </ScrollView>

        {/* Result count */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.textFaint,
            marginBottom: 14,
          }}
        >
          {t(
            filtered.length === 1
              ? "learn.encyclopedia_screens.medications.count_one"
              : "learn.encyclopedia_screens.medications.count_other",
            { params: { count: String(filtered.length) } },
          )}
        </Text>

        {/* 2x2 Grid */}
        <View style={{ gap: 12 }}>
          {rows.map((row, rowIdx) => (
            <View key={rowIdx} style={{ flexDirection: "row", gap: 12 }}>
              {row.map((med) => {
                const { body } = med;
                const firstRoute = body.commonRoutes[0];
                return (
                  <Pressable
                    key={med.id}
                    onPress={() => onItemPress(med)}
                    style={{
                      ...cardSurface(),
                      flex: 1,
                      overflow: "hidden",
                    }}
                  >
                    {/* Icon */}
                    <View
                      style={{
                        alignItems: "center",
                        paddingTop: 24,
                        paddingBottom: 12,
                      }}
                    >
                      <Image
                        source={getMedImage(body)}
                        style={{ width: 52, height: 52 }}
                        resizeMode="contain"
                      />
                    </View>

                    {/* Content */}
                    <View
                      style={{
                        padding: 16,
                        paddingTop: 4,
                        gap: 6,
                        alignItems: "center",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 15,
                          fontWeight: "700",
                          color: EMBER.colors.text,
                          lineHeight: 20,
                          textAlign: "center",
                        }}
                        numberOfLines={2}
                      >
                        {med.title}
                      </Text>

                      {fieldsFor(body).genericName ? (
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 12,
                            color: EMBER.colors.textLight,
                            lineHeight: 17,
                            textAlign: "center",
                          }}
                          numberOfLines={1}
                        >
                          {fieldsFor(body).genericName}
                        </Text>
                      ) : null}

                      {/* Tags */}
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          justifyContent: "center",
                          gap: 4,
                          marginTop: 4,
                        }}
                      >
                        <View
                          style={{
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                            borderRadius: 8,
                            backgroundColor: semanticColors.surface.muted,
                            borderWidth: 1,
                            borderColor: semanticColors.border.subtle,
                          }}
                        >
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 10,
                              fontWeight: "500",
                              color: EMBER.colors.textMid,
                              letterSpacing: 0.2,
                            }}
                          >
                            {firstRoute
                              ? t(
                                  `learn.encyclopedia_screens.medications.routes.${firstRoute}`,
                                )
                              : fieldsFor(body).drugClass}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </Pressable>
                );
              })}
              {row.length === 1 && <View style={{ flex: 1 }} />}
            </View>
          ))}
        </View>

        {/* Empty state */}
        {filtered.length === 0 && (
          <SearchEmptyState
            title={t("learn.encyclopedia_screens.medications.empty")}
            onReset={() => {
              setSearch("");
              setActiveClass(null);
            }}
          />
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
